import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel, Select,
  MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Chip, IconButton, CircularProgress, Alert, Snackbar,
  Tabs, Tab, LinearProgress, Accordion, AccordionSummary, AccordionDetails,
  useMediaQuery, useTheme,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon, Delete as DeleteIcon, Receipt as ReceiptIcon,
  ExpandMore as ExpandMoreIcon, Savings as BudgetIcon,
  CalendarMonth as MonthIcon, Edit as EditIcon,
  TrendingUp as TrendUpIcon, TrendingDown as TrendDownIcon,
} from '@mui/icons-material';
import apiService from '../../services/api';

const CATEGORY_LABELS: Record<string, string> = {
  combustible: 'Combustible',
  compras: 'Compras generales',
  gps: 'Equipos GPS',
  materiales: 'Materiales',
  herramientas: 'Herramientas',
  dieta: 'Dieta',
  peajes: 'Peajes',
  repuestos: 'Repuestos',
  mantenimiento: 'Mantenimiento',
  publicidad: 'Publicidad',
  comisiones: 'Comisiones por referidos',
  servidores: 'Servidores/Hosting',
  simcards: 'SIM/Datos (SimBase, recargas)',
  otros: 'Otros',
};
const CATEGORY_COLORS: Record<string, string> = {
  combustible: '#e0701a', compras: '#3a6ea5', gps: '#1565c0', materiales: '#7cb342',
  herramientas: '#795548', dieta: '#2f8a4e', peajes: '#8e44ad',
  repuestos: '#c0392b', mantenimiento: '#16807a', publicidad: '#d81b60', comisiones: '#5e35b1',
  servidores: '#455a64', simcards: '#00acc1', otros: '#6f675b',
};
const PAYMENT_LABELS: Record<string, string> = {
  cash: 'Efectivo', bank_transfer: 'Transferencia', card: 'Tarjeta', other: 'Otro',
};
const CATEGORIES = ['combustible', 'compras', 'gps', 'materiales', 'herramientas', 'dieta', 'peajes', 'repuestos', 'mantenimiento', 'publicidad', 'comisiones', 'servidores', 'simcards', 'otros'];

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];

/** 'YYYY-MM' → 'agosto 2026' */
const nombreMes = (m: string) => {
  const [a, mm] = m.split('-').map(Number);
  return `${MESES[mm - 1]} ${a}`;
};

const mesActualStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/**
 * Color del avance del presupuesto. La idea es que se vea de lejos si vamos bien:
 * verde tranquilo, amarillo ojo, naranja se está agotando, rojo ya se pasó.
 */
const colorAvance = (pct: number | null): 'success' | 'warning' | 'error' | 'inherit' => {
  if (pct === null) return 'inherit';
  if (pct >= 100) return 'error';
  if (pct >= 85) return 'warning';
  return 'success';
};

const money = (n: number, currency = 'DOP') =>
  `${currency === 'USD' ? 'US$' : 'RD$'}${(n || 0).toLocaleString('es-DO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const todayStr = () => new Date().toISOString().split('T')[0];

const ExpensesPage: React.FC = () => {
  const [expenses, setExpenses] = useState<any[]>([]);
  const [totals, setTotals] = useState<{ byCategory: Record<string, any>; grandTotal: number }>({ byCategory: {}, grandTotal: 0 });
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState<{ open: boolean; msg: string; sev: 'success' | 'error' }>({ open: false, msg: '', sev: 'success' });
  const [form, setForm] = useState({
    category: 'combustible', amount: '', currency: 'DOP', exchangeRate: '61', date: todayStr(), description: '', paymentMethod: 'cash',
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tab, setTab] = useState(0);
  const [months, setMonths] = useState<any[]>([]);
  const [loadingMonths, setLoadingMonths] = useState(false);
  const [budgetMonth, setBudgetMonth] = useState(mesActualStr());
  const [budgets, setBudgets] = useState<any>(null);
  const [loadingBudgets, setLoadingBudgets] = useState(false);
  const [budgetDialog, setBudgetDialog] = useState(false);
  const [budgetForm, setBudgetForm] = useState({ category: 'combustible', amount: '', currency: 'DOP', exchangeRate: '61', notes: '' });
  const [savingBudget, setSavingBudget] = useState(false);

  const loadMonths = useCallback(async () => {
    setLoadingMonths(true);
    try {
      const res: any = await apiService.getExpensesMonthly(12);
      setMonths(res?.data?.months || []);
    } catch {
      setSnack({ open: true, msg: 'Error cargando el resumen mensual', sev: 'error' });
    } finally {
      setLoadingMonths(false);
    }
  }, []);

  const loadBudgets = useCallback(async (mes: string) => {
    setLoadingBudgets(true);
    try {
      const res: any = await apiService.getBudgets(mes);
      setBudgets(res?.data || null);
    } catch {
      setSnack({ open: true, msg: 'Error cargando presupuestos', sev: 'error' });
    } finally {
      setLoadingBudgets(false);
    }
  }, []);

  useEffect(() => { if (tab === 0) loadMonths(); }, [tab, loadMonths]);
  useEffect(() => { if (tab === 1) loadBudgets(budgetMonth); }, [tab, budgetMonth, loadBudgets]);

  const handleSaveBudget = async () => {
    const monto = Number(budgetForm.amount);
    if (!Number.isFinite(monto) || monto <= 0) {
      setSnack({ open: true, msg: 'El monto debe ser mayor a 0', sev: 'error' });
      return;
    }
    setSavingBudget(true);
    try {
      await apiService.saveBudget({
        category: budgetForm.category,
        month: budgetMonth,
        amount: monto,
        currency: budgetForm.currency,
        exchangeRate: budgetForm.currency === 'USD' ? Number(budgetForm.exchangeRate) || 1 : 1,
        notes: budgetForm.notes,
      });
      setBudgetDialog(false);
      setBudgetForm({ category: 'combustible', amount: '', currency: 'DOP', exchangeRate: '61', notes: '' });
      setSnack({ open: true, msg: 'Presupuesto guardado', sev: 'success' });
      loadBudgets(budgetMonth);
    } catch (e: any) {
      setSnack({ open: true, msg: e?.response?.data?.message || 'Error al guardar', sev: 'error' });
    } finally {
      setSavingBudget(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res: any = await apiService.getExpenses(filterCategory ? { category: filterCategory } : undefined);
      setExpenses(res?.data?.expenses || []);
      setTotals(res?.data?.totals || { byCategory: {}, grandTotal: 0 });
    } catch (e: any) {
      setSnack({ open: true, msg: 'Error cargando gastos', sev: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterCategory]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      setSnack({ open: true, msg: 'Ingresa un monto válido', sev: 'error' });
      return;
    }
    if (form.currency === 'USD' && (!form.exchangeRate || Number(form.exchangeRate) <= 0)) {
      setSnack({ open: true, msg: 'Ingresa la tasa del dólar (RD$ por US$)', sev: 'error' });
      return;
    }
    setSaving(true);
    try {
      await apiService.createExpense({
        category: form.category,
        amount: Number(form.amount),
        currency: form.currency,
        exchangeRate: form.currency === 'USD' ? Number(form.exchangeRate) : 1,
        date: form.date,
        description: form.description || undefined,
        paymentMethod: form.paymentMethod,
      });
      setDialogOpen(false);
      setForm({ category: 'combustible', amount: '', currency: 'DOP', exchangeRate: '61', date: todayStr(), description: '', paymentMethod: 'cash' });
      setSnack({ open: true, msg: 'Gasto registrado', sev: 'success' });
      load();
    } catch (e: any) {
      setSnack({ open: true, msg: e?.response?.data?.message || 'Error al registrar', sev: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('¿Eliminar este gasto?')) return;
    try {
      await apiService.deleteExpense(id);
      setSnack({ open: true, msg: 'Gasto eliminado', sev: 'success' });
      load();
    } catch (e: any) {
      setSnack({ open: true, msg: 'Error al eliminar', sev: 'error' });
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptIcon /> Gastos
          </Typography>
          <Typography variant="body2" color="text.secondary">Combustible, compras, dieta, peajes, repuestos, mantenimiento y otros</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} size="large" onClick={() => setDialogOpen(true)}>
          Agregar Gasto
        </Button>
      </Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant={isMobile ? 'fullWidth' : 'standard'}
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab icon={<MonthIcon />} iconPosition="start" label={isMobile ? 'Meses' : 'Resumen mensual'} />
        <Tab icon={<BudgetIcon />} iconPosition="start" label="Presupuesto" />
        <Tab icon={<ReceiptIcon />} iconPosition="start" label={isMobile ? 'Lista' : 'Movimientos'} />
      </Tabs>

      {/* ─── Resumen mensual ─────────────────────────────────────────── */}
      {tab === 0 && (
        loadingMonths ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Últimos 12 meses. Toca un mes para ver en qué se fue.
            </Typography>
            {months.map((m, i) => {
              const anterior = months[i + 1];
              const dif = anterior && anterior.total > 0 ? ((m.total - anterior.total) / anterior.total) * 100 : null;
              const cats = Object.entries(m.byCategory || {}).sort((a: any, b: any) => b[1].total - a[1].total);
              return (
                <Accordion key={m.month} disableGutters variant="outlined" sx={{ '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', flexWrap: 'wrap', pr: 1 }}>
                      <Typography sx={{ fontWeight: 600, textTransform: 'capitalize', minWidth: 130 }}>
                        {nombreMes(m.month)}
                      </Typography>
                      <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                        {money(m.total)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.count} {m.count === 1 ? 'gasto' : 'gastos'}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {dif !== null && Math.abs(dif) >= 1 && (
                        <Chip
                          size="small"
                          icon={dif > 0 ? <TrendUpIcon /> : <TrendDownIcon />}
                          color={dif > 0 ? 'warning' : 'success'}
                          variant="outlined"
                          label={`${dif > 0 ? '+' : ''}${dif.toFixed(0)}% vs mes anterior`}
                        />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    {cats.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Sin gastos este mes.</Typography>
                    ) : (
                      <Grid container spacing={1}>
                        {cats.map(([cat, v]: any) => (
                          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cat}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: CATEGORY_COLORS[cat] || '#999', flexShrink: 0 }} />
                              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                                {CATEGORY_LABELS[cat] || cat}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                                {money(v.total)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 44, textAlign: 'right' }}>
                                {m.total > 0 ? `${Math.round((v.total / m.total) * 100)}%` : ''}
                              </Typography>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
        )
      )}

      {/* ─── Presupuesto ─────────────────────────────────────────────── */}
      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1.5, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              type="month" size="small" label="Mes" value={budgetMonth}
              onChange={(e) => setBudgetMonth(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 160 }}
            />
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setBudgetDialog(true)}>
              Definir presupuesto
            </Button>
          </Box>

          {loadingBudgets ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
          ) : !budgets || budgets.items.length === 0 ? (
            <Alert severity="info">
              Todavía no hay presupuesto ni gastos en {nombreMes(budgetMonth)}. Define uno para
              empezar a medir cuánto llevas gastado de lo que planificaste.
            </Alert>
          ) : (
            <>
              {/* Total del mes */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, textTransform: 'capitalize' }}>
                      {nombreMes(budgets.month)}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                      {money(budgets.summary.totalSpent)}
                      {budgets.summary.totalBudget > 0 && (
                        <Typography component="span" color="text.secondary" sx={{ fontWeight: 400 }}>
                          {' '}de {money(budgets.summary.totalBudget)}
                        </Typography>
                      )}
                    </Typography>
                  </Box>
                  {budgets.summary.percent !== null && (
                    <>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(budgets.summary.percent, 100)}
                        color={colorAvance(budgets.summary.percent) as any}
                        sx={{ height: 12, borderRadius: 6 }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {budgets.summary.percent}% consumido ·{' '}
                        {budgets.summary.remaining >= 0
                          ? `quedan ${money(budgets.summary.remaining)}`
                          : `pasado por ${money(Math.abs(budgets.summary.remaining))}`}
                      </Typography>
                    </>
                  )}
                  {budgets.summary.unbudgetedSpent > 0 && (
                    <Alert severity="info" sx={{ mt: 1.5, py: 0 }}>
                      {money(budgets.summary.unbudgetedSpent)} se gastó en categorías sin presupuesto definido.
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Por categoría */}
              <Grid container spacing={1.5}>
                {budgets.items.map((it: any) => (
                  <Grid size={{ xs: 12, md: 6 }} key={it.category}>
                    <Card variant="outlined" sx={{ borderLeft: `4px solid ${CATEGORY_COLORS[it.category] || '#999'}` }}>
                      <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>{it.label}</Typography>
                          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                            <strong>{money(it.spent)}</strong>
                            {it.hasBudget && <span style={{ opacity: 0.7 }}> de {money(it.budget)}</span>}
                          </Typography>
                        </Box>

                        {it.hasBudget ? (
                          <Box sx={{ mt: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(it.percent ?? 0, 100)}
                              color={colorAvance(it.percent) as any}
                              sx={{ height: 10, borderRadius: 5 }}
                            />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5, gap: 1, flexWrap: 'wrap' }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: `${colorAvance(it.percent)}.main` }}>
                                {it.percent}% {it.percent >= 100 ? '— presupuesto agotado' : it.percent >= 85 ? '— se está agotando' : ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {it.remaining >= 0 ? `quedan ${money(it.remaining)}` : `pasado por ${money(Math.abs(it.remaining))}`}
                              </Typography>
                            </Box>
                            {it.budgetOriginal?.currency === 'USD' && (
                              <Typography variant="caption" color="text.secondary">
                                Presupuestado en US${it.budgetOriginal.amount} (tasa {it.budgetOriginal.exchangeRate})
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Box sx={{ mt: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                              Sin presupuesto definido
                            </Typography>
                            <Button size="small" startIcon={<EditIcon />} onClick={() => {
                              setBudgetForm({ category: it.category, amount: '', currency: 'DOP', exchangeRate: '61', notes: '' });
                              setBudgetDialog(true);
                            }}>Definir</Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      )}

      {/* ─── Movimientos (lista de siempre) ──────────────────────────── */}
      {tab === 2 && (
      <>
      {/* Totales */}
      <Grid container spacing={1.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 4, md: 3 }}>
          <Card sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <CardContent>
              <Typography variant="caption">Total gastos</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{money(totals.grandTotal)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        {CATEGORIES.filter(c => totals.byCategory[c]).map(c => (
          <Grid size={{ xs: 6, sm: 4, md: 3 }} key={c}>
            <Card variant="outlined" sx={{ borderLeft: `4px solid ${CATEGORY_COLORS[c]}` }}>
              <CardContent sx={{ py: 1.5 }}>
                <Typography variant="caption" color="text.secondary">{CATEGORY_LABELS[c]}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(totals.byCategory[c].total)}</Typography>
                <Typography variant="caption" color="text.secondary">{totals.byCategory[c].count} registro(s)</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Filtro */}
      <Box sx={{ mb: 2, maxWidth: 260 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Filtrar categoría</InputLabel>
          <Select value={filterCategory} label="Filtrar categoría" onChange={(e) => setFilterCategory(e.target.value)}>
            <MenuItem value="">Todas</MenuItem>
            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>)}
          </Select>
        </FormControl>
      </Box>

      {/* Lista */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
      ) : expenses.length === 0 ? (
        <Alert severity="info">No hay gastos registrados{filterCategory ? ' en esta categoría' : ''}.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell>Método</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell align="right"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expenses.map((ex) => (
                <TableRow key={ex._id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{new Date(ex.date).toLocaleDateString('es-DO')}</TableCell>
                  <TableCell>
                    <Chip size="small" label={CATEGORY_LABELS[ex.category] || ex.category}
                      sx={{ bgcolor: CATEGORY_COLORS[ex.category] || '#888', color: '#fff' }} />
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{money(ex.amount, ex.currency)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{PAYMENT_LABELS[ex.paymentMethod] || ex.paymentMethod}</TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>{ex.description || '—'}</TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => handleDelete(ex._id)}><DeleteIcon fontSize="small" /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      </>
      )}

      {/* Dialogo definir presupuesto */}
      <Dialog open={budgetDialog} onClose={() => setBudgetDialog(false)} maxWidth="xs" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BudgetIcon /> Presupuesto de {nombreMes(budgetMonth)}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel>Categoría</InputLabel>
                <Select value={budgetForm.category} label="Categoría"
                  onChange={(e) => setBudgetForm({ ...budgetForm, category: e.target.value })}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select value={budgetForm.currency} label="Moneda"
                  onChange={(e) => setBudgetForm({ ...budgetForm, currency: e.target.value })}>
                  <MenuItem value="DOP">RD$ (pesos)</MenuItem>
                  <MenuItem value="USD">US$ (dólares)</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="number" label="Monto" value={budgetForm.amount}
                onChange={(e) => setBudgetForm({ ...budgetForm, amount: e.target.value })} />
            </Grid>
            {budgetForm.currency === 'USD' && (
              <Grid size={12}>
                <TextField fullWidth type="number" label="Tasa RD$ por US$" value={budgetForm.exchangeRate}
                  onChange={(e) => setBudgetForm({ ...budgetForm, exchangeRate: e.target.value })}
                  helperText={`Se compara contra el gasto real en RD$ — equivale a ${money((Number(budgetForm.amount) || 0) * (Number(budgetForm.exchangeRate) || 0))}`} />
              </Grid>
            )}
            <Grid size={12}>
              <TextField fullWidth label="Nota (opcional)" value={budgetForm.notes}
                onChange={(e) => setBudgetForm({ ...budgetForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBudgetDialog(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveBudget} disabled={savingBudget}>
            {savingBudget ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogo agregar */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ReceiptIcon /> Agregar Gasto</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Categoría</InputLabel>
                <Select value={form.category} label="Categoría" onChange={(e) => setForm(p => ({ ...p, category: e.target.value }))}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{CATEGORY_LABELS[c]}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 8, sm: 4 }}>
              <TextField fullWidth required type="number" label="Monto" value={form.amount}
                onChange={(e) => setForm(p => ({ ...p, amount: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 4, sm: 2 }}>
              <FormControl fullWidth>
                <InputLabel>Moneda</InputLabel>
                <Select value={form.currency} label="Moneda" onChange={(e) => setForm(p => ({ ...p, currency: e.target.value }))}>
                  <MenuItem value="DOP">RD$</MenuItem>
                  <MenuItem value="USD">US$</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {form.currency === 'USD' && (
              <Grid size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  type="number"
                  label="Tasa del dólar (RD$ por US$)"
                  value={form.exchangeRate}
                  onChange={(e) => setForm(p => ({ ...p, exchangeRate: e.target.value }))}
                  helperText={
                    form.amount && Number(form.exchangeRate) > 0
                      ? `Equivale a ${money(Number(form.amount) * Number(form.exchangeRate))}`
                      : 'Ej. 61 (spot ~59 + 2 por el cambio)'
                  }
                />
              </Grid>
            )}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField fullWidth type="date" label="Fecha" InputLabelProps={{ shrink: true }}
                value={form.date} onChange={(e) => setForm(p => ({ ...p, date: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Método de pago</InputLabel>
                <Select value={form.paymentMethod} label="Método de pago" onChange={(e) => setForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                  {Object.entries(PAYMENT_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField fullWidth multiline rows={2} label="Descripción (opcional)"
                value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} startIcon={<AddIcon />}>
            {saving ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snack.sev} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ExpensesPage;
